import React from 'react';
import { useSearchParams } from 'react-router-dom';

// ── Props ─────────────────────────────────────────────────────────────────────

export interface LinkCellProps {
  /** Text rendered inside the cell */
  label?: string | null;
  /** URL search-param key that will be set on click (e.g. 'workflow_id') */
  filterName?: string;
  /** Value written to filterName on click (e.g. entity.id) */
  filterValue?: string | number | null;
  /** Fully custom click handler — when provided, filterName/filterValue are ignored */
  onClick?: () => void;
  /** Extra inline style for the anchor element */
  style?: React.CSSProperties;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function LinkCell({
  label,
  filterName,
  filterValue,
  onClick,
  style,
}: LinkCellProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  if (!label) {
    return <span style={s.empty}>—</span>;
  }

  const isClickable = !!(onClick || (filterName && filterValue != null));

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onClick) {
      onClick();
      return;
    }
    if (filterName && filterValue != null) {
      const p = new URLSearchParams(searchParams);
      p.set(filterName, String(filterValue));
      p.set('page', '1');
      setSearchParams(p);
    }
  };

  if (!isClickable) {
    return <span style={{ ...s.text, ...style }}>{label}</span>;
  }

  return (
    <button type="button" style={{ ...s.link, ...style }} onClick={handleClick} title={`Filtrar por: ${label}`}>
      {label}
    </button>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  empty: {
    fontSize: 12,
    color: '#94a3b8',
  },
  text: {
    fontSize: 12,
    color: '#1e293b',
  },
  link: {
    all: 'unset',
    fontSize: 12,
    fontWeight: 600,
    color: '#6366f1',
    textDecoration: 'underline',
    textUnderlineOffset: 2,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
    display: 'inline-block',
  },
};
